<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LiaAgentsAPI from 'dashboard/api/liaAgents';

const props = defineProps({
  initialTab: {
    type: String,
    default: null,
  },
  agent: {
    type: Object,
    default: null,
  },
  erpConfig: {
    type: Object,
    default: () => ({}),
  },
  externalApisConfig: {
    type: Object,
    default: () => ({}),
  },
  companyDataConfig: {
    type: Object,
    default: () => ({
      name: '',
      phone: '',
      whatsapp: '',
      email: '',
      website: '',
      address: '',
      business_hours: '',
    }),
  },
  teamsConfig: {
    type: Array,
    default: () => [],
  },
  workflowConfig: {
    type: Object,
    default: () => ({
      ai_model: 'gpt-4.1-mini',
      ai_model_vision: 'gpt-4.1-mini',
      context_window: 15,
      buffer_wait_seconds: 12,
      typing_delay_min: 3,
      typing_delay_max: 7,
      max_message_chunks: 3,
      restart_keyword: 'restart',
      restart_message: 'Conversa reiniciada com sucesso.',
      image_analysis_prompt: '',
      knowledge_top_k: 3,
    }),
  },
  workflowToggles: {
    type: Object,
    default: () => ({}),
  },
  calendarConfig: {
    type: Object,
    default: () => ({
      google_calendar_id: '',
    }),
  },
});

const emit = defineEmits([
  'update:erpConfig',
  'update:externalApisConfig',
  'update:companyDataConfig',
  'update:teamsConfig',
  'update:workflowConfig',
  'update:workflowToggles',
  'update:calendarConfig',
  'saveExternalApis',
  'saveCompanyData',
  'saveTeams',
  'saveTools',
  'saveWorkflowConfig',
  'saveWorkflowToggles',
  'saveCalendar',
  'refresh',
]);

const { t } = useI18n();

// eslint-disable-next-line vue/no-bare-strings-in-template
const companyDataExample = '{{empresa_phone}}';

// Active settings tab (controlled by parent via initialTab, or internal navigation)
const activeSettingsTab = ref(props.initialTab || 'integrations');

// When parent controls the tab, sync it
watch(
  () => props.initialTab,
  newTab => {
    if (newTab) activeSettingsTab.value = newTab;
  }
);

// Whether parent controls tab navigation (hide internal tabs)
const externalTabControl = computed(() => !!props.initialTab);

// Local state
const isSavingErp = ref(false);
const isTestingErpConnection = ref(false);
const erpConnectionStatus = ref(null);

const isSavingExternalApis = ref(false);
const isTestingNaviApi = ref(false);
const naviConnectionStatus = ref(null);

const isSavingCompanyData = ref(false);
const isSavingTools = ref(false);
const isSavingTeams = ref(false);
const isSavingWorkflowConfig = ref(false);
const isSavingCalendar = ref(false);

// Helper to format company variable name
const formatCompanyVar = key => `{{empresa_${key}}}`;

// Local tools state (for toggle changes)
const localDisabledTools = ref([]);

// Initialize disabled tools from agent settings
const initializeDisabledTools = () => {
  localDisabledTools.value = [...(props.agent?.settings?.disabled_tools || [])];
};

// Get account teams for selection
const accountTeams = computed(() => {
  return props.agent?.account_teams || [];
});

// Check if agent is LIA 2.1
const isLia21 = computed(() => {
  const version = props.agent?.lia_version || props.agent?.version || '';
  return version.includes('2.1');
});

// Check if agent is LIA 3.1 (ERP integration)
const isLia31 = computed(() => {
  const version = props.agent?.lia_version || props.agent?.version || '';
  // Check version string OR if has ERP integration
  const hasErpIntegration =
    !!props.agent?.integration_id || !!props.agent?.integration_provider;
  return version.includes('3.1') || hasErpIntegration;
});

// Get ERP provider display name
const erpProviderName = computed(() => {
  // Check integration provider first, then template name for provider hint
  let provider = props.agent?.integration_provider;

  // If no integration provider, try to get from template name or lia_version
  if (!provider) {
    const templateName = props.agent?.template_name || '';
    const liaVersion = props.agent?.lia_version || '';

    if (
      templateName.toLowerCase().includes('mycore') ||
      liaVersion.toLowerCase().includes('mycore')
    ) {
      provider = 'mycore';
    } else if (
      templateName.toLowerCase().includes('ixc') ||
      liaVersion.toLowerCase().includes('ixc')
    ) {
      provider = 'ixc';
    } else if (
      templateName.toLowerCase().includes('sgp') ||
      liaVersion.toLowerCase().includes('sgp')
    ) {
      provider = 'sgp';
    } else if (
      templateName.toLowerCase().includes('voalle') ||
      liaVersion.toLowerCase().includes('voalle')
    ) {
      provider = 'voalle';
    } else if (
      templateName.toLowerCase().includes('hubsoft') ||
      liaVersion.toLowerCase().includes('hubsoft')
    ) {
      provider = 'hubsoft';
    }
  }

  const names = {
    ixc: 'IXC',
    mycore: 'MyCore',
    sgp: 'SGP',
    voalle: 'Voalle',
    hubsoft: 'HubSoft',
    mk_solutions: 'MK Solutions',
  };
  return names[provider] || provider?.toUpperCase() || 'ERP';
});

// Get required credentials for current provider
const erpRequiredCredentials = computed(() => {
  return (
    props.agent?.integration_provider_info?.required_credentials || [
      'api_url',
      'api_token',
    ]
  );
});

// Check if credentials are saved
const erpCredentialsSaved = computed(() => {
  const masked = props.agent?.integration_credentials_masked;
  return !!(
    masked?.api_url ||
    masked?.api_token ||
    masked?.client_id ||
    masked?.client_secret
  );
});

// Check if all required credentials are filled
const allCredentialsFilled = computed(() => {
  return erpRequiredCredentials.value.every(field => {
    const value = props.erpConfig[field];
    return value && value.trim() !== '';
  });
});

// Check if external APIs are saved
const externalApisSaved = computed(() => {
  const apis = props.agent?.settings?.external_apis;
  return !!(apis?.navi_api_url || apis?.navi_api_token);
});

// Get template tools
const templateTools = computed(() => {
  return props.agent?.template_tools || [];
});

// Check if transfer module is enabled (for showing Teams tab)
const transferEnabled = computed(() => {
  return props.agent?.settings?.modules?.transferencia !== false;
});

// Check if a tool is enabled
const isToolEnabled = toolId => {
  return !localDisabledTools.value.includes(toolId);
};

// Toggle tool enabled/disabled
const toggleTool = toolId => {
  const index = localDisabledTools.value.indexOf(toolId);
  if (index === -1) {
    localDisabledTools.value.push(toolId);
  } else {
    localDisabledTools.value.splice(index, 1);
  }
};

// Credential field config
const credentialConfig = computed(() => ({
  api_url: {
    label: t('LIA_AGENTS.SETTINGS.CREDENTIAL_API_URL_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.CREDENTIAL_API_URL_PLACEHOLDER'),
    type: 'url',
    icon: 'i-lucide-link',
  },
  api_token: {
    label: t('LIA_AGENTS.SETTINGS.CREDENTIAL_API_TOKEN_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.CREDENTIAL_API_TOKEN_PLACEHOLDER'),
    type: 'password',
    icon: 'i-lucide-key',
  },
  client_id: {
    label: t('LIA_AGENTS.SETTINGS.CREDENTIAL_CLIENT_ID_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.CREDENTIAL_CLIENT_ID_PLACEHOLDER'),
    type: 'text',
    icon: 'i-lucide-user',
  },
  client_secret: {
    label: t('LIA_AGENTS.SETTINGS.CREDENTIAL_CLIENT_SECRET_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.CREDENTIAL_CLIENT_SECRET_PLACEHOLDER'),
    type: 'password',
    icon: 'i-lucide-lock',
  },
}));

// Company data fields config
const companyDataFields = computed(() => [
  {
    key: 'name',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_NAME_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_NAME_PLACEHOLDER'),
    icon: 'i-lucide-building-2',
    type: 'text',
  },
  {
    key: 'phone',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_PHONE_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_PHONE_PLACEHOLDER'),
    icon: 'i-lucide-phone',
    type: 'tel',
  },
  {
    key: 'whatsapp',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_WHATSAPP_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_WHATSAPP_PLACEHOLDER'),
    icon: 'i-lucide-message-circle',
    type: 'tel',
  },
  {
    key: 'email',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_EMAIL_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_EMAIL_PLACEHOLDER'),
    icon: 'i-lucide-mail',
    type: 'email',
  },
  {
    key: 'website',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_WEBSITE_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_WEBSITE_PLACEHOLDER'),
    icon: 'i-lucide-globe',
    type: 'url',
  },
  {
    key: 'address',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_ADDRESS_LABEL'),
    placeholder: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_ADDRESS_PLACEHOLDER'),
    icon: 'i-lucide-map-pin',
    type: 'text',
  },
  {
    key: 'business_hours',
    label: t('LIA_AGENTS.SETTINGS.COMPANY_FIELD_BUSINESS_HOURS_LABEL'),
    placeholder: t(
      'LIA_AGENTS.SETTINGS.COMPANY_FIELD_BUSINESS_HOURS_PLACEHOLDER'
    ),
    icon: 'i-lucide-clock',
    type: 'text',
  },
]);

// Tool icons by capability type
const toolIcons = {
  consultar_viabilidade: 'i-lucide-map-pin',
  apresentar_planos: 'i-lucide-list',
  transferir_comercial: 'i-lucide-arrow-right-left',
  emitir_segunda_via: 'i-lucide-file-text',
  desbloqueio: 'i-lucide-unlock',
  transferir_financeiro: 'i-lucide-arrow-right-left',
  verificar_pendencias: 'i-lucide-search',
  diagnostico_conexao: 'i-lucide-wifi',
  transferir_suporte: 'i-lucide-arrow-right-left',
  consultar_faturas_abertas: 'i-lucide-receipt',
  consultar_faturas_atrasadas: 'i-lucide-alert-circle',
  consultar_faturas_pagas: 'i-lucide-check-circle',
  promessa_pagamento: 'i-lucide-handshake',
  verificar_status: 'i-lucide-activity',
  consultar_conexao_pppoe: 'i-lucide-network',
  reiniciar_conexao: 'i-lucide-refresh-cw',
  abrir_chamado: 'i-lucide-ticket',
  diagnostico_basico: 'i-lucide-stethoscope',
};

const isSavingToggles = ref(false);

// Toggle definitions grouped by category
const toggleGroups = computed(() => [
  {
    id: 'recursos',
    label: t('LIA_AGENTS.SETTINGS.TOGGLES_GROUP_RECURSOS'),
    icon: 'i-lucide-zap',
    items: [
      {
        key: 'analisa_imagem',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_ANALISA_IMAGEM_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_ANALISA_IMAGEM_DESC'),
        icon: 'i-lucide-image',
      },
      {
        key: 'transcreve_audio',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSCREVE_AUDIO_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSCREVE_AUDIO_DESC'),
        icon: 'i-lucide-mic',
      },
      {
        key: 'divide_resposta',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_DIVIDE_RESPOSTA_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_DIVIDE_RESPOSTA_DESC'),
        icon: 'i-lucide-split',
      },
      {
        key: 'filtra_csat',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_FILTRA_CSAT_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_FILTRA_CSAT_DESC'),
        icon: 'i-lucide-star',
      },
      {
        key: 'comando_restart',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_COMANDO_RESTART_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_COMANDO_RESTART_DESC'),
        icon: 'i-lucide-refresh-cw',
      },
    ],
  },
  {
    id: 'ferramentas',
    label: t('LIA_AGENTS.SETTINGS.TOGGLES_GROUP_FERRAMENTAS'),
    icon: 'i-lucide-wrench',
    items: [
      {
        key: 'ferramenta_segunda_via',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_SEGUNDA_VIA_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_SEGUNDA_VIA_DESC'),
        icon: 'i-lucide-file-text',
        agent: t('LIA_AGENTS.SETTINGS.TOGGLE_SEGUNDA_VIA_AGENT'),
      },
      {
        key: 'ferramenta_desbloqueio',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_DESBLOQUEIO_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_DESBLOQUEIO_DESC'),
        icon: 'i-lucide-unlock',
        agent: t('LIA_AGENTS.SETTINGS.TOGGLE_DESBLOQUEIO_AGENT'),
      },
      {
        key: 'ferramenta_verifica_bloqueio',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_VERIFICA_BLOQUEIO_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_VERIFICA_BLOQUEIO_DESC'),
        icon: 'i-lucide-search',
        agent: t('LIA_AGENTS.SETTINGS.TOGGLE_VERIFICA_BLOQUEIO_AGENT'),
      },
      {
        key: 'finalizar_atendimento',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_FINALIZAR_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_FINALIZAR_DESC'),
        icon: 'i-lucide-check-circle',
        agent: t('LIA_AGENTS.SETTINGS.TOGGLE_FINALIZAR_AGENT'),
      },
    ],
  },
  {
    id: 'transferencias',
    label: t('LIA_AGENTS.SETTINGS.TOGGLES_GROUP_TRANSFERENCIAS'),
    icon: 'i-lucide-arrow-right-left',
    items: [
      {
        key: 'transferir_suporte',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSFER_SUPORTE_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSFER_SUPORTE_DESC'),
        icon: 'i-lucide-headphones',
      },
      {
        key: 'transferir_financeiro',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSFER_FINANCEIRO_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSFER_FINANCEIRO_DESC'),
        icon: 'i-lucide-wallet',
      },
      {
        key: 'transferir_comercial',
        label: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSFER_COMERCIAL_LABEL'),
        description: t('LIA_AGENTS.SETTINGS.TOGGLE_TRANSFER_COMERCIAL_DESC'),
        icon: 'i-lucide-shopping-bag',
      },
    ],
  },
]);

// Check if a toggle is enabled (default true if not set)
const isToggleEnabled = key => {
  return props.workflowToggles[key] !== false;
};

// Toggle a workflow feature
const toggleWorkflowFeature = key => {
  const current = isToggleEnabled(key);
  emit('update:workflowToggles', {
    ...props.workflowToggles,
    [key]: !current,
  });
};

// Save workflow toggles
const saveWorkflowToggles = async () => {
  isSavingToggles.value = true;
  try {
    emit('saveWorkflowToggles');
    useAlert(t('LIA_AGENTS.SETTINGS.TOGGLES_SAVED'));
  } catch (error) {
    useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.message}`);
  } finally {
    isSavingToggles.value = false;
  }
};

// Update workflow config field
const updateWorkflowConfigField = (field, value) => {
  emit('update:workflowConfig', { ...props.workflowConfig, [field]: value });
};

// Save Workflow Config
const saveWorkflowConfigSettings = async () => {
  isSavingWorkflowConfig.value = true;
  try {
    emit('saveWorkflowConfig');
    useAlert(t('LIA_AGENTS.SETTINGS.ADVANCED_SAVED'));
  } catch (error) {
    useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.message}`);
  } finally {
    isSavingWorkflowConfig.value = false;
  }
};

// Update functions
const updateErpField = (field, value) => {
  emit('update:erpConfig', { ...props.erpConfig, [field]: value });
};

const updateExternalApisField = (field, value) => {
  emit('update:externalApisConfig', {
    ...props.externalApisConfig,
    [field]: value,
  });
};

const updateCompanyDataField = (field, value) => {
  emit('update:companyDataConfig', {
    ...props.companyDataConfig,
    [field]: value,
  });
};

const addTeam = () => {
  emit('update:teamsConfig', [...props.teamsConfig, { key: '', team_id: '' }]);
};

const removeTeam = index => {
  const updated = [...props.teamsConfig];
  updated.splice(index, 1);
  emit('update:teamsConfig', updated);
};

const updateTeamKey = (index, value) => {
  const updated = [...props.teamsConfig];
  updated[index] = { ...updated[index], key: value.toLowerCase().trim() };
  emit('update:teamsConfig', updated);
};

const updateTeamId = (index, value) => {
  const updated = [...props.teamsConfig];
  updated[index] = { ...updated[index], team_id: value ? Number(value) : '' };
  emit('update:teamsConfig', updated);
};

// Save ERP credentials
const saveErpSettings = async () => {
  isSavingErp.value = true;
  erpConnectionStatus.value = null;

  try {
    const credentials = {};
    const masked = props.agent?.integration_credentials_masked;

    erpRequiredCredentials.value.forEach(field => {
      const currentValue = props.erpConfig[field];
      const maskedValue = masked?.[field];

      if (currentValue && (!maskedValue || currentValue !== maskedValue)) {
        credentials[field] = currentValue;
      }
    });

    if (Object.keys(credentials).length === 0 && erpCredentialsSaved.value) {
      useAlert(t('LIA_AGENTS.SETTINGS.NO_CHANGES_DETECTED'));
      isSavingErp.value = false;
      return;
    }

    const response = await LiaAgentsAPI.updateIntegration(props.agent.id, {
      provider: props.erpConfig.provider,
      credentials,
    });

    if (response.data.success) {
      erpConnectionStatus.value = 'success';
      useAlert(t('LIA_AGENTS.SETTINGS.CREDENTIALS_SAVED_SUCCESS'));
      emit('refresh');
    } else {
      erpConnectionStatus.value = 'error';
      useAlert(
        `${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${response.data.errors?.join(', ') || t('LIA_AGENTS.EDIT.SAVE_ERROR')}`
      );
    }
  } catch (error) {
    erpConnectionStatus.value = 'error';
    useAlert(
      `${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.response?.data?.error || error.message}`
    );
  } finally {
    isSavingErp.value = false;
  }
};

// Test ERP connection
const testErpConnection = async () => {
  isTestingErpConnection.value = true;
  erpConnectionStatus.value = null;

  try {
    await saveErpSettings();
  } catch {
    erpConnectionStatus.value = 'error';
  } finally {
    isTestingErpConnection.value = false;
  }
};

// Save External APIs config
const saveExternalApisConfig = async () => {
  isSavingExternalApis.value = true;
  try {
    emit('saveExternalApis');
    useAlert(t('LIA_AGENTS.SETTINGS.EXTERNAL_APIS_SAVED'));
  } catch (error) {
    useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.message}`);
  } finally {
    isSavingExternalApis.value = false;
  }
};

// Test Navi API connection
const testNaviApi = async () => {
  isTestingNaviApi.value = true;
  naviConnectionStatus.value = null;
  try {
    const response = await LiaAgentsAPI.testNaviApi(
      props.agent.id,
      props.externalApisConfig.navi_api_url,
      props.externalApisConfig.navi_api_token
    );
    if (response.data.success) {
      naviConnectionStatus.value = 'success';
      useAlert(t('LIA_AGENTS.SETTINGS.NAVI_CONNECTED'));
    } else {
      naviConnectionStatus.value = 'error';
      useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${response.data.message}`);
    }
  } catch (error) {
    naviConnectionStatus.value = 'error';
    useAlert(
      `${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.response?.data?.message || error.message}`
    );
  } finally {
    isTestingNaviApi.value = false;
  }
};

// Save Company Data config
const saveCompanyDataConfig = async () => {
  isSavingCompanyData.value = true;
  try {
    emit('saveCompanyData');
    useAlert(t('LIA_AGENTS.SETTINGS.COMPANY_DATA_SAVED'));
  } catch (error) {
    useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.message}`);
  } finally {
    isSavingCompanyData.value = false;
  }
};

// Save Tools config
const saveToolsConfig = async () => {
  isSavingTools.value = true;
  try {
    emit('saveTools', localDisabledTools.value);
    useAlert(t('LIA_AGENTS.SETTINGS.TOOLS_SAVED'));
  } catch (error) {
    useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.message}`);
  } finally {
    isSavingTools.value = false;
  }
};

// Save Teams config
const saveTeamsConfig = async () => {
  isSavingTeams.value = true;
  try {
    emit('saveTeams');
    useAlert(t('LIA_AGENTS.SETTINGS.TEAMS_SAVED'));
  } catch (error) {
    useAlert(
      `${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.response?.data?.error || error.message}`
    );
  } finally {
    isSavingTeams.value = false;
  }
};

// Update calendar config field
const updateCalendarField = (field, value) => {
  emit('update:calendarConfig', { ...props.calendarConfig, [field]: value });
};

// Save Calendar config
const saveCalendarConfig = async () => {
  isSavingCalendar.value = true;
  try {
    emit('saveCalendar');
    useAlert(t('LIA_AGENTS.SETTINGS.CALENDAR_SAVED'));
  } catch (error) {
    useAlert(`${t('LIA_AGENTS.EDIT.SAVE_ERROR')}: ${error.message}`);
  } finally {
    isSavingCalendar.value = false;
  }
};

// Check if calendar is configured
const calendarConfigured = computed(() => {
  return !!props.calendarConfig?.google_calendar_id;
});

// Check if provider is MyCore (calls ERP directly, no Navi API needed)
const isMyCore = computed(() => {
  const provider = props.agent?.integration_provider;
  const templateName = props.agent?.template_name || '';
  const liaVersion = props.agent?.lia_version || '';

  return (
    provider === 'mycore' ||
    templateName.toLowerCase().includes('mycore') ||
    liaVersion.toLowerCase().includes('mycore')
  );
});

// Settings tabs
const settingsTabs = computed(() => {
  const tabs = [];

  // Integrações (ERP) - only for LIA 3.1
  if (isLia31.value) {
    tabs.push({
      id: 'integrations',
      label: t('LIA_AGENTS.SETTINGS.TAB_INTEGRATIONS'),
      icon: 'i-lucide-plug',
    });
  }

  // API Navi only for non-MyCore providers (MyCore calls ERP directly)
  if (!isMyCore.value) {
    tabs.push({
      id: 'navi',
      label: t('LIA_AGENTS.SETTINGS.TAB_NAVI'),
      icon: 'i-lucide-globe',
    });
  }

  // Company data always available
  tabs.push({
    id: 'company',
    label: t('LIA_AGENTS.SETTINGS.TAB_COMPANY'),
    icon: 'i-lucide-building-2',
  });

  // NOTE: Tools tab removed - the toggles were not actually controlling
  // workflow behavior. Tool enable/disable would require dynamic workflow
  // modification which is not currently implemented.

  // Teams for LIA 2.1 and 3.1 with transfer enabled
  if ((isLia31.value || isLia21.value) && transferEnabled.value) {
    tabs.push({
      id: 'teams',
      label: t('LIA_AGENTS.SETTINGS.TAB_TEAMS'),
      icon: 'i-lucide-users',
    });
  }

  // Calendar for LIA 2.1
  if (isLia21.value) {
    tabs.push({
      id: 'calendar',
      label: t('LIA_AGENTS.SETTINGS.TAB_CALENDAR'),
      icon: 'i-lucide-calendar',
    });
  }

  // Workflow controls (toggle features/tools) for LIA 3.1
  if (isLia31.value) {
    tabs.push({
      id: 'controls',
      label: t('LIA_AGENTS.SETTINGS.TAB_CONTROLS'),
      icon: 'i-lucide-toggle-right',
    });
  }

  // Advanced workflow config always available for LIA 3.1
  if (isLia31.value) {
    tabs.push({
      id: 'advanced',
      label: t('LIA_AGENTS.SETTINGS.TAB_ADVANCED'),
      icon: 'i-lucide-sliders-horizontal',
    });
  }

  return tabs;
});

// Initialize on mount
initializeDisabledTools();

// Watch for agent changes to set the correct default tab
watch(
  () => props.agent,
  newAgent => {
    if (!newAgent) return;
    // Set default tab based on version and provider
    const version = newAgent.lia_version || newAgent.version || '';
    const is31 = version.includes('3.1');
    const templateName = newAgent.template_name || '';
    const liaVersion = newAgent.lia_version || '';
    const isMyCoreAgent =
      newAgent.integration_provider === 'mycore' ||
      templateName.toLowerCase().includes('mycore') ||
      liaVersion.toLowerCase().includes('mycore');

    // Switch away from navi tab if it won't exist
    if (activeSettingsTab.value === 'navi') {
      if (is31) {
        activeSettingsTab.value = 'integrations';
      } else if (isMyCoreAgent) {
        activeSettingsTab.value = 'company';
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="space-y-6">
    <!-- Settings Tabs -->
    <div class="bg-n-solid-3 rounded-xl overflow-hidden border border-n-weak">
      <!-- Tab Navigation (hidden when parent controls tabs) -->
      <div v-if="!externalTabControl" class="flex border-b border-n-weak">
        <button
          v-for="tab in settingsTabs"
          :key="tab.id"
          type="button"
          class="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px"
          :class="
            activeSettingsTab === tab.id
              ? 'border-woot-500 text-woot-600 dark:text-woot-400 bg-woot-50/50 dark:bg-woot-900/10'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
          @click="activeSettingsTab = tab.id"
        >
          <span :class="tab.icon" class="size-4" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="p-5">
        <!-- Integrations Tab (ERP only for LIA 3.1) -->
        <div v-if="activeSettingsTab === 'integrations'" class="space-y-4">
          <!-- ERP Integration (only for LIA 3.1) -->
          <div v-if="isLia31" class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div
                  class="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center"
                >
                  <span
                    class="i-lucide-database size-4 text-purple-600 dark:text-purple-400"
                  />
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ erpProviderName }}
                  </h4>
                  <p class="text-xs text-n-slate-11">
                    {{ t('LIA_AGENTS.SETTINGS.ERP_CREDENTIALS_TITLE') }}
                  </p>
                </div>
              </div>
              <span
                v-if="erpCredentialsSaved"
                class="flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full"
              >
                <span class="i-lucide-check size-3" />
                {{ t('LIA_AGENTS.SETTINGS.ERP_CONFIGURED') }}
              </span>
            </div>

            <!-- Credential Fields -->
            <div class="grid gap-3">
              <div
                v-for="field in erpRequiredCredentials"
                :key="field"
                class="flex items-center gap-3"
              >
                <span
                  :class="credentialConfig[field]?.icon"
                  class="size-4 text-n-slate-11 flex-shrink-0"
                />
                <div class="flex-1">
                  <input
                    :value="erpConfig[field]"
                    :type="
                      erpCredentialsSaved
                        ? 'text'
                        : credentialConfig[field]?.type || 'text'
                    "
                    :placeholder="credentialConfig[field]?.placeholder"
                    class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    :class="{ 'font-mono': erpCredentialsSaved }"
                    @input="updateErpField(field, $event.target.value)"
                  />
                </div>
              </div>
            </div>

            <!-- Connection Status -->
            <div
              v-if="erpConnectionStatus"
              class="flex items-center gap-2 p-2 rounded-lg text-sm"
              :class="
                erpConnectionStatus === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              "
            >
              <span
                :class="
                  erpConnectionStatus === 'success'
                    ? 'i-lucide-check-circle'
                    : 'i-lucide-x-circle'
                "
                class="size-4"
              />
              {{
                erpConnectionStatus === 'success'
                  ? t('LIA_AGENTS.SETTINGS.CONNECTION_OK')
                  : t('LIA_AGENTS.SETTINGS.CONNECTION_FAILED')
              }}
            </div>

            <!-- Actions -->
            <div class="flex gap-2">
              <button
                class="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                :disabled="isSavingErp || !allCredentialsFilled"
                @click="saveErpSettings"
              >
                <span
                  v-if="isSavingErp"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                <span v-else class="i-lucide-save size-4" />
                {{ t('LIA_AGENTS.SETTINGS.SAVE') }}
              </button>
              <button
                class="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50"
                :disabled="isTestingErpConnection || !allCredentialsFilled"
                @click="testErpConnection"
              >
                <span
                  v-if="isTestingErpConnection"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                <span v-else class="i-lucide-plug-zap size-4" />
                {{ t('LIA_AGENTS.SETTINGS.TEST') }}
              </button>
            </div>
          </div>

          <!-- Info for non-LIA 3.1 -->
          <div v-if="!isLia31" class="p-4 bg-n-alpha-2 rounded-lg">
            <p class="text-sm text-n-slate-11 flex items-start gap-2">
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.NON_LIA31_INFO') }}
            </p>
          </div>
        </div>

        <!-- API Navi Tab -->
        <div v-if="activeSettingsTab === 'navi'" class="space-y-4">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-globe size-5 text-blue-600 dark:text-blue-400"
              />
            </div>
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ $t('LIA_AGENTS.SETTINGS.NAVI_TITLE') }}
              </h4>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.NAVI_SUBTITLE') }}
              </p>
            </div>
            <span
              v-if="externalApisSaved"
              class="ml-auto flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full"
            >
              <span class="i-lucide-check size-3" />
              {{ $t('LIA_AGENTS.SETTINGS.NAVI_CONFIGURED') }}
            </span>
          </div>

          <!-- Navi Fields -->
          <div class="grid gap-3">
            <div class="flex items-center gap-3">
              <span
                class="i-lucide-link size-4 text-n-slate-11 flex-shrink-0"
              />
              <div class="flex-1">
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('LIA_AGENTS.SETTINGS.NAVI_URL_FIELD_LABEL') }}
                </label>
                <input
                  :value="externalApisConfig.navi_api_url"
                  type="url"
                  :placeholder="$t('LIA_AGENTS.SETTINGS.NAVI_URL_PLACEHOLDER')"
                  class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  @input="
                    updateExternalApisField('navi_api_url', $event.target.value)
                  "
                />
              </div>
            </div>
            <div class="flex items-center gap-3">
              <span class="i-lucide-key size-4 text-n-slate-11 flex-shrink-0" />
              <div class="flex-1">
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('LIA_AGENTS.SETTINGS.NAVI_TOKEN_FIELD_LABEL') }}
                </label>
                <input
                  :value="externalApisConfig.navi_api_token"
                  type="password"
                  :placeholder="
                    $t('LIA_AGENTS.SETTINGS.NAVI_TOKEN_FIELD_PLACEHOLDER')
                  "
                  class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  @input="
                    updateExternalApisField(
                      'navi_api_token',
                      $event.target.value
                    )
                  "
                />
              </div>
            </div>
          </div>

          <!-- Connection Status -->
          <div
            v-if="naviConnectionStatus"
            class="flex items-center gap-2 p-2 rounded-lg text-sm"
            :class="
              naviConnectionStatus === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            "
          >
            <span
              :class="
                naviConnectionStatus === 'success'
                  ? 'i-lucide-check-circle'
                  : 'i-lucide-x-circle'
              "
              class="size-4"
            />
            {{
              naviConnectionStatus === 'success'
                ? t('LIA_AGENTS.SETTINGS.CONNECTION_OK')
                : t('LIA_AGENTS.SETTINGS.CONNECTION_FAILED')
            }}
          </div>

          <!-- Actions -->
          <div class="flex gap-2">
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              :disabled="isSavingExternalApis"
              @click="saveExternalApisConfig"
            >
              <span
                v-if="isSavingExternalApis"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-save size-4" />
              {{ t('LIA_AGENTS.SETTINGS.SAVE') }}
            </button>
            <button
              class="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
              :disabled="
                isTestingNaviApi ||
                !externalApisConfig.navi_api_url ||
                !externalApisConfig.navi_api_token
              "
              @click="testNaviApi"
            >
              <span
                v-if="isTestingNaviApi"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-plug-zap size-4" />
              {{ t('LIA_AGENTS.SETTINGS.TEST') }}
            </button>
          </div>

          <!-- Info -->
          <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p
              class="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2"
            >
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.NAVI_INFO') }}
            </p>
          </div>
        </div>

        <!-- Company Data Tab -->
        <div v-if="activeSettingsTab === 'company'" class="space-y-4">
          <!-- Info -->
          <div class="p-3 bg-woot-50 dark:bg-woot-900/20 rounded-lg">
            <p
              class="text-xs text-woot-700 dark:text-woot-300 flex items-start gap-2"
            >
              <span class="i-lucide-lightbulb size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.COMPANY_DATA_INFO') }}
              <code class="bg-woot-100 dark:bg-woot-800 px-1 rounded">{{
                $t('LIA_AGENTS.SETTINGS.COMPANY_DATA_EXAMPLE', {
                  example: companyDataExample,
                })
              }}</code>
            </p>
          </div>

          <!-- Company Data Fields -->
          <div class="grid gap-3">
            <div
              v-for="field in companyDataFields"
              :key="field.key"
              class="flex items-center gap-3"
            >
              <span
                :class="field.icon"
                class="size-4 text-n-slate-11 flex-shrink-0"
              />
              <div class="flex-1">
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ field.label }}
                </label>
                <input
                  :value="companyDataConfig[field.key]"
                  :type="field.type"
                  :placeholder="field.placeholder"
                  class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500 text-sm"
                  @input="
                    updateCompanyDataField(field.key, $event.target.value)
                  "
                />
              </div>
            </div>
          </div>

          <!-- Variables Reference -->
          <div class="p-3 bg-n-alpha-2 rounded-lg">
            <p class="text-xs font-medium text-n-slate-11 mb-2">
              {{ $t('LIA_AGENTS.SETTINGS.COMPANY_VARIABLES_TITLE') }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <code
                v-for="field in companyDataFields"
                :key="field.key"
                class="px-1.5 py-0.5 bg-n-alpha-3 text-n-slate-12 rounded text-xs font-mono"
              >
                {{ formatCompanyVar(field.key) }}
              </code>
            </div>
          </div>

          <!-- Save Button -->
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSavingCompanyData"
            @click="saveCompanyDataConfig"
          >
            <span
              v-if="isSavingCompanyData"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ $t('LIA_AGENTS.SETTINGS.SAVE_COMPANY_DATA') }}
          </button>
        </div>

        <!-- Tools Tab -->
        <div v-if="activeSettingsTab === 'tools'" class="space-y-4">
          <!-- Info -->
          <div class="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p
              class="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2"
            >
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.TOOLS_INFO') }}
            </p>
          </div>

          <!-- Tools List -->
          <div class="space-y-2">
            <label
              v-for="tool in templateTools"
              :key="tool.id"
              class="flex items-center gap-3 p-3 bg-n-alpha-2 rounded-lg cursor-pointer hover:bg-n-alpha-3 transition-colors"
            >
              <input
                :checked="isToolEnabled(tool.id)"
                type="checkbox"
                class="sr-only peer"
                @change="toggleTool(tool.id)"
              />
              <div
                class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                :class="
                  isToolEnabled(tool.id)
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-n-alpha-3'
                "
              >
                <span
                  :class="toolIcons[tool.id] || 'i-lucide-wrench'"
                  class="size-4"
                  :style="{
                    color: isToolEnabled(tool.id)
                      ? 'var(--color-green-600)'
                      : 'var(--color-n-slate-11)',
                  }"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="font-medium text-sm"
                  :class="
                    isToolEnabled(tool.id)
                      ? 'text-n-slate-12'
                      : 'text-n-slate-11'
                  "
                >
                  {{ tool.name }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ tool.agent }}
                </p>
              </div>
              <div
                class="w-10 h-5 rounded-full relative transition-colors"
                :class="
                  isToolEnabled(tool.id) ? 'bg-green-500' : 'bg-n-alpha-3'
                "
              >
                <div
                  class="absolute top-[2px] left-[2px] w-4 h-4 bg-white rounded-full transition-transform"
                  :class="isToolEnabled(tool.id) ? 'translate-x-5' : ''"
                />
              </div>
            </label>
          </div>

          <!-- Empty State -->
          <div v-if="templateTools.length === 0" class="text-center py-8">
            <div
              class="w-12 h-12 mx-auto mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-wrench size-6 text-n-slate-11" />
            </div>
            <p class="text-sm text-n-slate-11">
              {{ $t('LIA_AGENTS.SETTINGS.TOOLS_EMPTY') }}
            </p>
          </div>

          <!-- Save Button -->
          <button
            v-if="templateTools.length > 0"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSavingTools"
            @click="saveToolsConfig"
          >
            <span
              v-if="isSavingTools"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ $t('LIA_AGENTS.SETTINGS.SAVE_TOOLS') }}
          </button>
        </div>

        <!-- Calendar Tab (LIA 2.1) -->
        <div v-if="activeSettingsTab === 'calendar'" class="space-y-4">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-calendar size-5 text-orange-600 dark:text-orange-400"
              />
            </div>
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ $t('LIA_AGENTS.SETTINGS.CALENDAR_TITLE') }}
              </h4>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.CALENDAR_SUBTITLE') }}
              </p>
            </div>
            <span
              v-if="calendarConfigured"
              class="ml-auto flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full"
            >
              <span class="i-lucide-check size-3" />
              {{ $t('LIA_AGENTS.SETTINGS.CALENDAR_CONFIGURED') }}
            </span>
          </div>

          <!-- Calendar ID Field -->
          <div class="grid gap-3">
            <div class="flex items-center gap-3">
              <span
                class="i-lucide-calendar-days size-4 text-n-slate-11 flex-shrink-0"
              />
              <div class="flex-1">
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('LIA_AGENTS.SETTINGS.CALENDAR_ID_LABEL') }}
                </label>
                <input
                  :value="calendarConfig.google_calendar_id"
                  type="text"
                  :placeholder="
                    $t('LIA_AGENTS.SETTINGS.CALENDAR_ID_PLACEHOLDER')
                  "
                  class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm font-mono"
                  @input="
                    updateCalendarField(
                      'google_calendar_id',
                      $event.target.value
                    )
                  "
                />
              </div>
            </div>
          </div>

          <!-- Help text -->
          <div class="p-3 bg-n-alpha-2 rounded-lg">
            <p class="text-xs text-n-slate-11 flex items-start gap-2">
              <span class="i-lucide-help-circle size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.CALENDAR_ID_DESC') }}
            </p>
          </div>

          <!-- Info -->
          <div class="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <p
              class="text-xs text-orange-700 dark:text-orange-300 flex items-start gap-2"
            >
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.CALENDAR_INFO') }}
            </p>
          </div>

          <!-- Save Button -->
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSavingCalendar"
            @click="saveCalendarConfig"
          >
            <span
              v-if="isSavingCalendar"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ $t('LIA_AGENTS.SETTINGS.SAVE_CALENDAR') }}
          </button>
        </div>

        <!-- Controls Tab -->
        <div v-if="activeSettingsTab === 'controls'" class="space-y-5">
          <div class="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
            <p
              class="text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2"
            >
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.CONTROLS_INFO') }}
            </p>
          </div>

          <div v-for="group in toggleGroups" :key="group.id" class="space-y-3">
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span :class="group.icon" class="size-4 text-emerald-500" />
              {{ group.label }}
            </h4>

            <div class="space-y-2">
              <div
                v-for="item in group.items"
                :key="item.key"
                class="flex items-center gap-3 p-3 rounded-lg border-l-4 cursor-pointer transition-all"
                :class="
                  isToggleEnabled(item.key)
                    ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-500'
                    : 'bg-red-50/50 dark:bg-red-900/5 border-red-300 dark:border-red-800'
                "
                @click="toggleWorkflowFeature(item.key)"
              >
                <div
                  class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="
                    isToggleEnabled(item.key)
                      ? 'bg-emerald-100 dark:bg-emerald-900/30'
                      : 'bg-red-100 dark:bg-red-900/20'
                  "
                >
                  <span
                    :class="item.icon"
                    class="size-4"
                    :style="{
                      color: isToggleEnabled(item.key)
                        ? 'var(--color-green-600)'
                        : 'var(--color-red-400)',
                    }"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p
                      class="text-sm font-medium"
                      :class="
                        isToggleEnabled(item.key)
                          ? 'text-n-slate-12'
                          : 'text-n-slate-11 line-through'
                      "
                    >
                      {{ item.label }}
                    </p>
                    <span
                      v-if="item.agent"
                      class="px-1.5 py-0.5 text-xs font-medium bg-n-alpha-3 text-n-slate-11 rounded"
                    >
                      {{ item.agent }}
                    </span>
                  </div>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{ item.description }}
                  </p>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span
                    class="px-2 py-1 text-xs font-bold rounded-full"
                    :class="
                      isToggleEnabled(item.key)
                        ? 'bg-emerald-500 text-white'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                    "
                  >
                    {{
                      isToggleEnabled(item.key)
                        ? $t('LIA_AGENTS.SETTINGS.TOGGLE_ON')
                        : $t('LIA_AGENTS.SETTINGS.TOGGLE_OFF')
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSavingToggles"
            @click="saveWorkflowToggles"
          >
            <span
              v-if="isSavingToggles"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ $t('LIA_AGENTS.SETTINGS.SAVE_CONTROLS') }}
          </button>
        </div>

        <!-- Advanced Tab -->
        <div v-if="activeSettingsTab === 'advanced'" class="space-y-5">
          <!-- Info -->
          <div class="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <p
              class="text-xs text-indigo-700 dark:text-indigo-300 flex items-start gap-2"
            >
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ $t('LIA_AGENTS.SETTINGS.ADVANCED_INFO') }}
            </p>
          </div>

          <!-- ===== SECTION: Memoria e Contexto ===== -->
          <div class="space-y-4">
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-database size-4 text-indigo-500" />
              {{ $t('LIA_AGENTS.SETTINGS.SECTION_MEMORY') }}
            </h4>

            <!-- Context Window -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ $t('LIA_AGENTS.SETTINGS.CONTEXT_WINDOW_LABEL') }}
                </label>
                <span
                  class="text-sm font-mono text-indigo-600 dark:text-indigo-400"
                >
                  {{ workflowConfig.context_window }}
                  {{ $t('LIA_AGENTS.SETTINGS.CONTEXT_WINDOW_UNIT') }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.CONTEXT_WINDOW_DESC') }}
              </p>
              <input
                :value="workflowConfig.context_window"
                type="range"
                min="5"
                max="30"
                step="1"
                class="w-full accent-indigo-500"
                @input="
                  updateWorkflowConfigField(
                    'context_window',
                    Number($event.target.value)
                  )
                "
              />
              <div class="flex justify-between text-xs text-n-slate-11">
                <span>{{ 5 }}</span>
                <span>{{ 30 }}</span>
              </div>
            </div>

            <!-- Buffer Wait -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ $t('LIA_AGENTS.SETTINGS.BUFFER_WAIT_LABEL') }}
                </label>
                <span
                  class="text-sm font-mono text-indigo-600 dark:text-indigo-400"
                >
                  {{ `${workflowConfig.buffer_wait_seconds}s` }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.BUFFER_WAIT_DESC') }}
              </p>
              <input
                :value="workflowConfig.buffer_wait_seconds"
                type="range"
                min="5"
                max="20"
                step="1"
                class="w-full accent-indigo-500"
                @input="
                  updateWorkflowConfigField(
                    'buffer_wait_seconds',
                    Number($event.target.value)
                  )
                "
              />
              <div class="flex justify-between text-xs text-n-slate-11">
                <span>{{ '5s' }}</span>
                <span>{{ '20s' }}</span>
              </div>
            </div>

            <!-- Knowledge Top-K -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ $t('LIA_AGENTS.SETTINGS.KNOWLEDGE_TOPK_LABEL') }}
                </label>
                <span
                  class="text-sm font-mono text-indigo-600 dark:text-indigo-400"
                >
                  {{ workflowConfig.knowledge_top_k || 3 }}
                  {{ $t('LIA_AGENTS.SETTINGS.KNOWLEDGE_TOPK_UNIT') }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.KNOWLEDGE_TOPK_DESC') }}
              </p>
              <input
                :value="workflowConfig.knowledge_top_k || 3"
                type="range"
                min="1"
                max="10"
                step="1"
                class="w-full accent-indigo-500"
                @input="
                  updateWorkflowConfigField(
                    'knowledge_top_k',
                    Number($event.target.value)
                  )
                "
              />
              <div class="flex justify-between text-xs text-n-slate-11">
                <span>{{ 1 }}</span>
                <span>{{ 10 }}</span>
              </div>
            </div>
          </div>

          <hr class="border-n-weak" />

          <!-- ===== SECTION: Comportamento de Resposta ===== -->
          <div class="space-y-4">
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-message-square size-4 text-indigo-500" />
              {{ $t('LIA_AGENTS.SETTINGS.SECTION_RESPONSE') }}
            </h4>

            <!-- Typing Delay Min/Max -->
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <label class="block text-xs font-medium text-n-slate-12">
                    {{ $t('LIA_AGENTS.SETTINGS.TYPING_DELAY_MIN_LABEL') }}
                  </label>
                  <span
                    class="text-xs font-mono text-indigo-600 dark:text-indigo-400"
                  >
                    {{ `${workflowConfig.typing_delay_min || 3}s` }}
                  </span>
                </div>
                <input
                  :value="workflowConfig.typing_delay_min || 3"
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  class="w-full accent-indigo-500"
                  @input="
                    updateWorkflowConfigField(
                      'typing_delay_min',
                      Number($event.target.value)
                    )
                  "
                />
                <div class="flex justify-between text-xs text-n-slate-11">
                  <span>{{ '1s' }}</span>
                  <span>{{ '10s' }}</span>
                </div>
              </div>
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <label class="block text-xs font-medium text-n-slate-12">
                    {{ $t('LIA_AGENTS.SETTINGS.TYPING_DELAY_MAX_LABEL') }}
                  </label>
                  <span
                    class="text-xs font-mono text-indigo-600 dark:text-indigo-400"
                  >
                    {{ `${workflowConfig.typing_delay_max || 7}s` }}
                  </span>
                </div>
                <input
                  :value="workflowConfig.typing_delay_max || 7"
                  type="range"
                  min="2"
                  max="15"
                  step="1"
                  class="w-full accent-indigo-500"
                  @input="
                    updateWorkflowConfigField(
                      'typing_delay_max',
                      Number($event.target.value)
                    )
                  "
                />
                <div class="flex justify-between text-xs text-n-slate-11">
                  <span>{{ '2s' }}</span>
                  <span>{{ '15s' }}</span>
                </div>
              </div>
            </div>
            <p class="text-xs text-n-slate-11">
              {{ $t('LIA_AGENTS.SETTINGS.TYPING_DELAY_DESC') }}
            </p>

            <!-- Max Message Chunks -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ $t('LIA_AGENTS.SETTINGS.MAX_CHUNKS_LABEL') }}
                </label>
                <span
                  class="text-sm font-mono text-indigo-600 dark:text-indigo-400"
                >
                  {{
                    (workflowConfig.max_message_chunks || 3) === -1
                      ? $t('LIA_AGENTS.SETTINGS.MAX_CHUNKS_UNLIMITED')
                      : workflowConfig.max_message_chunks || 3
                  }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.MAX_CHUNKS_DESC') }}
              </p>
              <input
                :value="workflowConfig.max_message_chunks || 3"
                type="range"
                min="-1"
                max="10"
                step="1"
                class="w-full accent-indigo-500"
                @input="
                  updateWorkflowConfigField(
                    'max_message_chunks',
                    Number($event.target.value)
                  )
                "
              />
              <div class="flex justify-between text-xs text-n-slate-11">
                <span>{{
                  $t('LIA_AGENTS.SETTINGS.MAX_CHUNKS_UNLIMITED')
                }}</span>
                <span>{{ 10 }}</span>
              </div>
            </div>
          </div>

          <hr class="border-n-weak" />

          <!-- ===== SECTION: Reiniciar Conversa ===== -->
          <div class="space-y-4">
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-refresh-cw size-4 text-indigo-500" />
              {{ $t('LIA_AGENTS.SETTINGS.SECTION_RESTART') }}
            </h4>

            <!-- Restart Keyword -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ $t('LIA_AGENTS.SETTINGS.RESTART_KEYWORD_LABEL') }}
              </label>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.RESTART_KEYWORD_DESC') }}
              </p>
              <input
                :value="workflowConfig.restart_keyword || 'restart'"
                type="text"
                :placeholder="
                  $t('LIA_AGENTS.SETTINGS.RESTART_KEYWORD_PLACEHOLDER')
                "
                class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                @input="
                  updateWorkflowConfigField(
                    'restart_keyword',
                    $event.target.value
                  )
                "
              />
            </div>

            <!-- Restart Message -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ $t('LIA_AGENTS.SETTINGS.RESTART_MESSAGE_LABEL') }}
              </label>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.RESTART_MESSAGE_DESC') }}
              </p>
              <input
                :value="
                  workflowConfig.restart_message ||
                  'Conversa reiniciada com sucesso.'
                "
                type="text"
                :placeholder="$t('LIA_AGENTS.SETTINGS.RESTART_MESSAGE_DESC')"
                class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                @input="
                  updateWorkflowConfigField(
                    'restart_message',
                    $event.target.value
                  )
                "
              />
            </div>
          </div>

          <hr class="border-n-weak" />

          <!-- ===== SECTION: Analise de Imagem ===== -->
          <div class="space-y-4">
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-image size-4 text-indigo-500" />
              {{ $t('LIA_AGENTS.SETTINGS.SECTION_IMAGE') }}
            </h4>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ $t('LIA_AGENTS.SETTINGS.IMAGE_PROMPT_LABEL') }}
              </label>
              <p class="text-xs text-n-slate-11">
                {{ $t('LIA_AGENTS.SETTINGS.IMAGE_PROMPT_DESC') }}
              </p>
              <textarea
                :value="workflowConfig.image_analysis_prompt || ''"
                rows="5"
                :placeholder="
                  $t('LIA_AGENTS.SETTINGS.IMAGE_PROMPT_PLACEHOLDER')
                "
                class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                @input="
                  updateWorkflowConfigField(
                    'image_analysis_prompt',
                    $event.target.value
                  )
                "
              />
            </div>
          </div>

          <!-- Save Button -->
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSavingWorkflowConfig"
            @click="saveWorkflowConfigSettings"
          >
            <span
              v-if="isSavingWorkflowConfig"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ $t('LIA_AGENTS.SETTINGS.SAVE_ADVANCED') }}
          </button>
        </div>

        <!-- Teams Tab -->
        <div v-if="activeSettingsTab === 'teams'" class="space-y-4">
          <!-- Dynamic Team List -->
          <div
            v-for="(team, index) in teamsConfig"
            :key="index"
            class="flex items-center gap-3 p-3 bg-n-alpha-2 rounded-lg"
          >
            <div
              class="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0"
            >
              <span
                class="i-lucide-users size-4 text-orange-600 dark:text-orange-400"
              />
            </div>
            <div class="flex-1 min-w-0 grid grid-cols-2 gap-2">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('LIA_AGENTS.SETTINGS.TEAM_KEY_LABEL') }}
                </label>
                <input
                  :value="team.key"
                  type="text"
                  :placeholder="t('LIA_AGENTS.SETTINGS.TEAM_KEY_PLACEHOLDER')"
                  class="w-full px-3 py-1.5 bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  @input="updateTeamKey(index, $event.target.value)"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('LIA_AGENTS.SETTINGS.TEAM_SELECT_LABEL') }}
                </label>
                <select
                  :value="team.team_id"
                  class="w-full px-3 py-1.5 pr-8 bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 appearance-none select-caret"
                  @change="updateTeamId(index, $event.target.value)"
                >
                  <option value="">
                    {{ t('LIA_AGENTS.SETTINGS.TEAM_SELECT_PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="accountTeam in accountTeams"
                    :key="accountTeam.id"
                    :value="accountTeam.id"
                  >
                    {{ accountTeam.name }}
                  </option>
                </select>
              </div>
            </div>
            <button
              class="w-8 h-8 flex items-center justify-center rounded-lg text-n-slate-9 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
              @click="removeTeam(index)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>

          <!-- Empty State -->
          <div
            v-if="teamsConfig.length === 0"
            class="text-center py-6 text-n-slate-9"
          >
            <span class="i-lucide-users size-8 mx-auto mb-2 block" />
            <p class="text-sm">
              {{ t('LIA_AGENTS.SETTINGS.TEAMS_EMPTY') }}
            </p>
          </div>

          <!-- Add Team Button -->
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-orange-600 dark:text-orange-400 border border-dashed border-orange-300 dark:border-orange-700 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
            @click="addTeam"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('LIA_AGENTS.SETTINGS.TEAM_ADD') }}
          </button>

          <!-- Info -->
          <div class="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <p
              class="text-xs text-orange-700 dark:text-orange-300 flex items-start gap-2"
            >
              <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
              {{ t('LIA_AGENTS.SETTINGS.TEAMS_INFO') }}
            </p>
          </div>

          <!-- Save Button -->
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSavingTeams"
            @click="saveTeamsConfig"
          >
            <span
              v-if="isSavingTeams"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ t('LIA_AGENTS.SETTINGS.SAVE_TEAMS') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.select-caret {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none' stroke='rgb%28110,111,115%29' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M2 2.5 6 6 10 2.5'/></svg>");
  background-origin: content-box;
  background-position: right -1rem center;
  background-repeat: no-repeat;
  background-size: 10px 7px;
}
</style>
